import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/apiConfig.js';
import './MalwareBazaarTool.css';

const MalwareBazaarTool = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [fileName, setFileName] = useState(null);

  console.log('[MalwareBazaarTool] Component loaded');
  const API_BASE_URL = getApiBaseUrl();
  console.log('[MalwareBazaarTool] API_BASE_URL:', API_BASE_URL);

  const deliveryMethods = [
    { value: 'email_attachment', label: 'Email attachment' },
    { value: 'email_link', label: 'Email link' },
    { value: 'web_download', label: 'Web download' },
    { value: 'web_drive-by', label: 'Web drive-by' },
    { value: 'multiple', label: 'Multiple' },
    { value: 'other', label: 'Other' }
  ];

  const referenceTypes = [
    { value: 'anyRun', label: 'ANY.RUN analysis' },
    { value: 'joeSandbox', label: 'JoeSandbox report' },
    { value: 'malpedia', label: 'Malpedia entry' },
    { value: 'twitter', label: 'Twitter/X link' },
    { value: 'urlhaus', label: 'URLhaus entry' },
    { value: 'links', label: 'Other link' }
  ];

  const contextTypes = [
    { value: 'droppedByMd5', label: 'Dropped by (MD5)' },
    { value: 'droppedBySha256', label: 'Dropped by (SHA256)' },
    { value: 'droppedByMalware', label: 'Dropped by malware' },
    { value: 'droppingMd5', label: 'Drops (MD5)' },
    { value: 'droppingSha256', label: 'Drops (SHA256)' },
    { value: 'droppingMalware', label: 'Drops malware' }
  ];

  const [formData, setFormData] = useState({
    tags: '',
    deliveryMethod: 'other',
    anonymous: false,
    references: [],
    context: [],
    comment: ''
  });

  const [file, setFile] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addReference = (type, link) => {
    if (!link.trim()) {
      alert('Please enter a reference link');
      return;
    }
    setFormData(prev => ({
      ...prev,
      references: [...prev.references, { type, link: link.trim() }]
    }));
    // Clear the input
    document.getElementById(`reference-${type}`)?.parentElement?.querySelector('input')?.remove?.();
  };

  const removeReference = (index) => {
    setFormData(prev => ({
      ...prev,
      references: prev.references.filter((_, i) => i !== index)
    }));
  };

  const addContext = (type, value) => {
    if (!value.trim()) {
      alert('Please enter a context value');
      return;
    }
    setFormData(prev => ({
      ...prev,
      context: [...prev.context, { type, value: value.trim() }]
    }));
  };

  const removeContext = (index) => {
    setFormData(prev => ({
      ...prev,
      context: prev.context.filter((_, i) => i !== index)
    }));
  };

  const handleFileChange = (e) => {
    console.log('[MalwareBazaarTool] File input changed');
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      console.log('[MalwareBazaarTool] File selected:', selectedFile.name, selectedFile.size);
      setFile(selectedFile);
      setFileName(selectedFile.name);
    } else {
      console.log('[MalwareBazaarTool] No file selected');
    }
  };

  const handleSubmit = async (e) => {
    console.log('[MalwareBazaarTool] Form submit clicked');
    e.preventDefault();

    // Validation
    if (!file) {
      console.log('[MalwareBazaarTool] No file selected');
      alert('Please select a malware file to upload');
      return;
    }

    console.log('[MalwareBazaarTool] File ready for upload:', file.name);

    const token = localStorage.getItem('token');
    if (!token) {
      console.log('[MalwareBazaarTool] No token found');
      setSubmissionResult({
        success: false,
        message: 'Authentication required. Please log in first.'
      });
      return;
    }

    console.log('[MalwareBazaarTool] Token found, length:', token.length);

    const uploadUrl = `${API_BASE_URL}/malware-bazaar/upload`;
    console.log('[MalwareBazaarTool] Using API URL:', uploadUrl);

    setIsSubmitting(true);
    setSubmissionResult(null);

    try {
      // Prepare FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('file', file);
      formDataToSend.append('tags', formData.tags);
      formDataToSend.append('deliveryMethod', formData.deliveryMethod);
      formDataToSend.append('anonymous', formData.anonymous ? 1 : 0);
      
      // Convert references array to object
      const referencesObj = {};
      formData.references.forEach(ref => {
        if (!referencesObj[ref.type]) referencesObj[ref.type] = [];
        referencesObj[ref.type].push(ref.link);
      });
      
      // Convert context array to object
      const contextObj = {};
      formData.context.forEach(ctx => {
        if (!contextObj[ctx.type]) contextObj[ctx.type] = [];
        contextObj[ctx.type].push(ctx.value);
      });
      
      formDataToSend.append('references', JSON.stringify(referencesObj));
      formDataToSend.append('context', JSON.stringify(contextObj));
      formDataToSend.append('comment', formData.comment);

      console.log('[MalwareBazaarTool] FormData prepared, sending request...');

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      console.log('[MalwareBazaarTool] Response received:', response.status, response.statusText);

      const data = await response.json();

      console.log('[MalwareBazaarTool] Response data:', data);

      if (response.ok) {
        console.log('[MalwareBazaarTool] Upload successful:', data);

        setSubmissionResult({
          success: true,
          caseNumber: data.caseNumber,
          message: `Malware uploaded successfully! Case #${data.caseNumber}`,
          apiStatus: data.apiResponse?.query_status
        });

        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            tags: '',
            deliveryMethod: 'other',
            anonymous: false,
            references: [],
            context: [],
            comment: ''
          });
          setFile(null);
          setFileName(null);
          setSubmissionResult(null);
        }, 3000);
      } else {
        console.log('[MalwareBazaarTool] Upload failed:', data);
        setSubmissionResult({
          success: false,
          message: data.error || 'Failed to upload malware'
        });
      }
    } catch (error) {
      console.error('[MalwareBazaarTool] Exception during upload:', error);
      setSubmissionResult({
        success: false,
        message: `Upload error: ${error.message || 'An error occurred while uploading the malware'}`
      });
    } finally {
      console.log('[MalwareBazaarTool] Upload process finished');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="malware-bazaar-container">
      <button onClick={() => navigate('/tools')} className="back-button-malware">
        ← Back to Dashboard
      </button>

      <div className="malware-bazaar-header">
        <h1>🦠 Malware Bazaar Reporter</h1>
        <p className="subtitle">Report malware samples to abuse.ch Malware Bazaar</p>
      </div>

      {submissionResult && (
        <div className={`result-message ${submissionResult.success ? 'success' : 'error'}`}>
          <strong>{submissionResult.success ? '✓ Success!' : '✗ Error'}</strong>
          <p>{submissionResult.message}</p>
          {submissionResult.caseNumber && (
            <p className="case-number">Case #: {submissionResult.caseNumber}</p>
          )}
          {submissionResult.apiStatus && (
            <p className="api-status">API Status: {submissionResult.apiStatus}</p>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="malware-bazaar-form">
        {/* Malware Sample File Upload */}
        <section className="form-section">
          <h2>Malware Sample *</h2>
          <div className="form-group file-upload-group">
            <div className="file-input-wrapper">
              <input
                type="file"
                id="file"
                onChange={handleFileChange}
                className="file-input"
                required
              />
              <label htmlFor="file" className="file-name">
                {fileName || 'Choose file...'}
              </label>
            </div>
            <p className="helper-text">Max file size: 100 MB</p>
          </div>
        </section>

        {/* Tags */}
        <section className="form-section">
          <h2>Tags</h2>
          <div className="form-group">
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              placeholder="e.g., trojan, ransomware, worm (alphanumeric, dots, hyphens)"
            />
          </div>
        </section>

        {/* References */}
        <section className="form-section">
          <h2>Reference</h2>
          <div className="form-group">
            <div className="reference-input-group">
              <select
                id="refType"
                className="ref-type-select"
                onChange={(e) => {
                  const refType = e.target.value;
                  const refLink = document.getElementById('refLink').value;
                  if (refType && refLink) {
                    addReference(refType, refLink);
                    e.target.value = '';
                    document.getElementById('refLink').value = '';
                  }
                }}
                defaultValue=""
              >
                <option value="">Select reference type...</option>
                <option value="ANY.RUN">ANY.RUN</option>
                <option value="JoeSandbox">JoeSandbox</option>
                <option value="Malpedia">Malpedia</option>
                <option value="Twitter">Twitter/X</option>
                <option value="URLhaus">URLhaus</option>
                <option value="Other">Other</option>
              </select>
              <input
                type="url"
                id="refLink"
                className="ref-link-input"
                placeholder="Enter reference link..."
              />
              <button
                type="button"
                className="btn-add"
                onClick={() => {
                  const refType = document.getElementById('refType').value;
                  const refLink = document.getElementById('refLink').value;
                  if (refType && refLink) {
                    addReference(refType, refLink);
                    document.getElementById('refType').value = '';
                    document.getElementById('refLink').value = '';
                  }
                }}
              >
                Add
              </button>
            </div>
            {formData.references.length > 0 && (
              <div className="references-list">
                {formData.references.map((ref, idx) => (
                  <div key={idx} className="reference-item">
                    <span><strong>{ref.type}:</strong> {ref.link}</span>
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeReference(idx)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Context */}
        <section className="form-section">
          <h2>Context</h2>
          <div className="form-group">
            <div className="context-input-group">
              <select
                id="ctxType"
                className="ctx-type-select"
                onChange={(e) => {
                  const ctxType = e.target.value;
                  const ctxValue = document.getElementById('ctxValue').value;
                  if (ctxType && ctxValue) {
                    addContext(ctxType, ctxValue);
                    e.target.value = '';
                    document.getElementById('ctxValue').value = '';
                  }
                }}
                defaultValue=""
              >
                <option value="">Select context type...</option>
                <option value="Dropped by MD5">Dropped by MD5</option>
                <option value="Dropped by SHA256">Dropped by SHA256</option>
                <option value="Drops MD5">Drops MD5</option>
                <option value="Drops SHA256">Drops SHA256</option>
                <option value="Other">Other</option>
              </select>
              <input
                type="text"
                id="ctxValue"
                className="ctx-value-input"
                placeholder="Enter context value..."
              />
              <button
                type="button"
                className="btn-add"
                onClick={() => {
                  const ctxType = document.getElementById('ctxType').value;
                  const ctxValue = document.getElementById('ctxValue').value;
                  if (ctxType && ctxValue) {
                    addContext(ctxType, ctxValue);
                    document.getElementById('ctxType').value = '';
                    document.getElementById('ctxValue').value = '';
                  }
                }}
              >
                Add
              </button>
            </div>
            {formData.context.length > 0 && (
              <div className="context-list">
                {formData.context.map((ctx, idx) => (
                  <div key={idx} className="context-item">
                    <span><strong>{ctx.type}:</strong> {ctx.value}</span>
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeContext(idx)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Delivery Method */}
        <section className="form-section">
          <h2>Delivery Method</h2>
          <div className="form-group">
            <select
              id="deliveryMethod"
              name="deliveryMethod"
              value={formData.deliveryMethod}
              onChange={handleInputChange}
            >
              {deliveryMethods.map(method => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>
        </section>

        {/* Comment */}
        <section className="form-section">
          <h2>Comment</h2>
          <div className="form-group">
            <textarea
              id="comment"
              name="comment"
              value={formData.comment}
              onChange={handleInputChange}
              placeholder="Any additional information about this malware sample..."
              rows="3"
            />
          </div>
        </section>

        {/* Anonymous Submission */}
        <section className="form-section">
          <div className="form-group checkbox-group">
            <label htmlFor="anonymous">
              <input
                type="checkbox"
                id="anonymous"
                name="anonymous"
                checked={formData.anonymous}
                onChange={handleInputChange}
              />
              Submit Anonymously
            </label>
          </div>
        </section>

        {/* Submit Buttons */}
        <div className="form-actions">
          <button
            type="submit"
            className="btn-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Uploading...' : '📤 Upload to Malware Bazaar'}
          </button>
          <button
            type="button"
            className="btn-history"
            onClick={() => navigate('/malware-bazaar-history')}
          >
            📋 View History
          </button>
        </div>
      </form>

      <div className="info-section">
        <h3>ℹ️ Important Notes</h3>
        <ul>
          <li>Only submit <strong>confirmed malware</strong> samples - do not submit suspicious or benign files</li>
          <li>Do not submit adware (PUPs) or file infectors</li>
          <li>Preferred: Fresh malware samples (less than 10 days old)</li>
        </ul>
      </div>
    </div>
  );
};

export default MalwareBazaarTool;
